import React, { useContext, useEffect, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { LuBot, LuBrain, LuChartColumn, LuSparkles, LuCode, LuPlug } from "react-icons/lu";
import { Link, useNavigate } from "react-router";
import { Images } from "../controller/Images";
import { GlobalStateContext } from "../controller/GlobalState";
import { aiServicePages, localSeoFor } from "../controller/global";

const ICONS = {
  "/agentic-ai-development": LuBot,
  "/recommendation-engine-development": LuChartColumn,
  "/generative-ai-development": LuSparkles,
  "/custom-software-development": LuCode,
  "/ai-integration-services": LuPlug,
  "/llm-integration-services": LuBrain,
};

// Same trust-logo set shown on the individual AI service pages.
const TRUST_LOGOS = [
   // { name: "Aptor", src: Images.aptor_logo },
  { name: "BoxERP", src: Images.boxErp },
  { name: "GE", src: Images.ge_logo },
  { name: "Jennison", src: Images.Jennison_Dark_logo },
  { name: "Regent", src: Images.regent_logo },
  { name: "ThreeLockBox", src: Images.threelockbox_logo },
  { name: "Stokkd", src: Images.stokkd_logo },
  { name: "Ujala Enterprises", src: Images.UjalaEnterprises_logo },
];

const CHOOSER = [
  {
    num: "01 / AUTOMATE",
    title: "Already drowning in repetitive decisions?",
    copy: "Agentic AI Development builds autonomous systems that make and execute decisions within limits you define.",
    tags: ["Agentic AI", "Autonomous workflows", "Human oversight"],
    path: "/agentic-ai-development",
    cta: "Agentic AI development",
  },
  {
    num: "02 / PERSONALIZE",
    title: "Want to recommend the right thing, every time?",
    copy: "Recommendation Engine Development builds AI-powered personalization that boosts engagement and conversion.",
    tags: ["Recommendation Engine", "Personalization", "Real-time scoring"],
    path: "/recommendation-engine-development",
    cta: "Recommendation engine development",
  },
  {
    num: "03 / UNDERSTAND",
    title: "Need your team to talk to its own knowledge?",
    copy: "LLM Integration Services put language models on your own documents and systems, so answers are grounded in what you actually hold.",
    tags: ["LLM integration", "RAG", "Evaluation"],
    path: "/llm-integration-services",
    cta: "LLM integration services",
  },
  {
    num: "04 / CREATE",
    title: "Want to draft, summarize or generate at volume?",
    copy: "Generative AI Development builds the apps and copilots that produce work, with evaluation and cost limits from the start.",
    tags: ["Generative AI", "Copilots", "Guardrails"],
    path: "/generative-ai-development",
    cta: "Generative AI development",
  },
  {
    num: "05 / CONNECT",
    title: "Already running software AI should plug into?",
    copy: "AI Integration Services add AI to systems that are live today, without rebuilding what already works.",
    tags: ["AI integration", "Existing systems", "No rebuild"],
    path: "/ai-integration-services",
    cta: "AI integration services",
  },
];

const PROCESS_STEPS = [
  { n: "01", t: "Discover", d: "Identify the decision, workflow, or knowledge gap where AI actually creates value." },
  { n: "02", t: "Prove", d: "Ship a focused pilot around one measurable outcome, fast." },
  { n: "03", t: "Integrate", d: "Connect the system to your real data, tools, and permissions — not a demo environment." },
  { n: "04", t: "Operate", d: "Ongoing monitoring, evaluation, and support once it's live." },
];

const FIT_CRITERIA = [
  { a: "Need autonomous decisions?", b: "AGENTIC AI" },
  { a: "Need personalized recommendations?", b: "RECOMMENDATION ENGINE" },
  { a: "Need a knowledge assistant?", b: "GENERATIVE AI" },
  { a: "Need AI inside existing systems?", b: "AI INTEGRATION" },
];

const FACTS = [
  { a: "5", b: "Dedicated AI services" },
  { a: "7", b: "Stage delivery process" },
  { a: "MSME", b: "Registered IT company" },
  { a: "Delhi NCR", b: "Headquarters" },
];

// What moves the price (SEO plan: competitors all answer cost; we explain the
// drivers and quote after scoping rather than publish invented numbers).
const COST_FACTORS = [
  { t: "One use case or many", d: "Starting with one focused use case keeps the first phase small and proves value before you invest more." },
  { t: "Data readiness", d: "How clean and reachable your data is often matters more than the AI itself. Messy or scattered data adds preparation work." },
  { t: "Systems it must work with", d: "AI that reads from or acts in your existing software needs integration work for each system involved." },
  { t: "Accuracy and oversight", d: "Tasks where mistakes are costly need more testing, guardrails and human review than tasks where a person checks the output." },
];

const FAQS = [
  {
    q: "Do you build AI-powered applications?",
    a: "Yes — we have dedicated services for Generative AI Development, Agentic AI Development, Recommendation Engine Development, and AI Integration Services, each with its own page describing what we build.",
  },
  {
    q: "What does a first AI engagement look like?",
    a: "We begin with a focused discovery to identify the decision, knowledge, and success measure that matter most, typically followed by a working pilot within 4-6 weeks.",
  },
  {
    q: "How is an AI agent different from a chatbot?",
    a: "A chatbot answers questions. An agent plans and executes a sequence of steps against defined tools and systems, within explicit limits on what it's permitted to do, and reports back what it did.",
  },
  {
    q: "Which AI service is right for my business?",
    a: "Start from the problem. If you need AI inside existing systems, see AI integration; for language tasks such as answering from documents, LLM integration; for multi-step tasks done automatically, agentic AI; for new AI products, generative AI; and for product suggestions, recommendation engines.",
  },
  {
    q: "How much do AI development services cost?",
    a: "It depends on the use case, the data and how much of your existing software is involved. We scope one focused use case first and give a fixed estimate before any build starts.",
  },
  {
    q: "How long does an AI project take?",
    a: "A focused first use case usually reaches a working pilot in weeks. We ship one use case end to end before expanding, so value shows up early.",
  },
  {
    q: "Is our data safe when you build AI solutions?",
    a: "Data stays in your own cloud where possible, access follows your existing permissions, and we use providers and settings that do not train on your data.",
  },
  {
    q: "Who owns the code and the AI system you build?",
    a: "You do. Source code, prompts, evaluation sets, trained model weights and documentation are handed over and belong to you, and we sign an NDA before any detailed discussion.",
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

// Framer Motion writes its own `transform`, which silently overrides any CSS
// `transform: rotate()` on the same element — so the engine panel's tilt has
// to be animated in, not declared in CSS.
const fadeUpRotate = {
  hidden: { opacity: 0, y: 18, rotate: 0 },
  show: (i = 0) => ({ opacity: 1, y: 0, rotate: 1.5, transition: { duration: 0.6, delay: i * 0.06, ease: [0.22, 1, 0.36, 1] } }),
};

function Reveal({ as = "div", i = 0, className, children, reduceMotion, ...rest }) {
  const MotionTag = motion[as] || motion.div;
  return (
    <MotionTag
      className={className}
      custom={i}
      variants={fadeUp}
      initial={reduceMotion ? false : "hidden"}
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      {...rest}
    >
      {children}
    </MotionTag>
  );
}

// Overview / hub page for the "AI Solutions" nav menu — same ea-hero split
// panel shape as the five dedicated AI service pages it links to, so this
// page reads as part of the same family rather than a generic index. Every
// service card here links out to its dedicated page, and each of those
// pages links back here plus across to its four siblings.
const AISolutions = () => {
  const navigate = useNavigate();
  const { setIsOpen } = useContext(GlobalStateContext);
  const [openFaq, setOpenFaq] = useState(-1);
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    const seo = localSeoFor(
      "/ai-solutions",
      "AI Solutions | InQmise Technologies",
      "Explore InQmise Technologies' AI solutions — Generative AI, Agentic AI, Recommendation Engines, Custom Software, and AI Integration Services."
    );
    document.title = seo.title;

    const description = document.createElement("meta");
    description.name = "description";
    description.content = seo.description;
    document.head.appendChild(description);

    let canonical = document.querySelector("link[rel='canonical']");
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://inqmisetechnologies.com/ai-solutions");

    const faqScript = document.createElement("script");
    faqScript.type = "application/ld+json";
    faqScript.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQS.map((item) => ({
        "@type": "Question",
        "name": item.q,
        "acceptedAnswer": { "@type": "Answer", "text": item.a },
      })),
    });
    document.head.appendChild(faqScript);
  }, []);

  return (
    <main className="editorial-ai pt-20">
      {/* HERO — same split panel shape as the individual AI service pages */}
      <header className="ea-hero ea-wrap">
        <Reveal as="div" reduceMotion={reduceMotion}>
          <p className="ea-eyebrow">AI Solutions</p>
          <h1>
            AI development services: six ways to bring <em>real AI</em> into your business.
          </h1>
          <p className="ea-lede">
            From generative AI and autonomous agents to recommendation engines and full AI integration — pick the entry point that matches where your business is today.
          </p>
          <div className="ea-actions">
            <button className="ea-button ea-primary" onClick={() => setIsOpen(true)} aria-label="Open contact form to plan your AI project">
              Plan your AI project ↗
            </button>
            <a className="ea-text-link" href="/services" onClick={(e) => { e.preventDefault(); navigate("/services"); }}>All services</a>
          </div>
        </Reveal>

        <Reveal as="aside" i={1} reduceMotion={reduceMotion} variants={fadeUpRotate} className="ea-engine">
          <div className="ea-engine-top"><span /> INQMISE / AI SOLUTIONS <b>LIVE</b></div>
          <h2>Five dedicated AI services, one accountable team.</h2>
          <div className="ea-query"><small>{aiServicePages[2].title.toUpperCase()}</small>{aiServicePages[2].desc}</div>
          <div className="ea-query"><small>{aiServicePages[0].title.toUpperCase()}</small>{aiServicePages[0].desc}</div>
          <div className="ea-flow"><b>Scope</b><i>→</i><b>Build</b><i>→</i><b>Integrate</b></div>
        </Reveal>
      </header>

      {/* TRUST LOGOS */}
      <div className="ea-wrap ea-logo-row">
        <span>TRUSTED BY GROWING BUSINESSES</span>
        <div className="ea-logo-track">
          <div className="ea-logo-track-inner">
            {[...TRUST_LOGOS, ...TRUST_LOGOS].map((logo, i) => (
              <img key={`${logo.name}-${i}`} src={logo.src} alt={logo.name} loading="lazy" />
            ))}
          </div>
        </div>
      </div>

      {/* HOW TO CHOOSE — feature rows */}
      <section className="ea-wrap ea-section" id="choose">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How to choose</div>
            <h2>Start from the<br />problem, not the tech.</h2>
          </div>
          <p>Five common starting points, and the service that actually fits each one.</p>
        </Reveal>

        {CHOOSER.map((item, i) => (
          <Reveal as="article" i={i + 1} reduceMotion={reduceMotion} className="ea-feature" key={item.title}>
            <div className="ea-num">{item.num}</div>
            <h3>{item.title}</h3>
            <p>{item.copy}</p>
            <div className="ea-tags">
              {item.tags.map((tag) => <span key={tag}>{tag}</span>)}
            </div>
            <Link className="ea-text-link" to={item.path}>
              {item.cta} &#8599;
            </Link>
          </Reveal>
        ))}
      </section>

      {/* PROCESS */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">How we work</div>
            <h2>Discover, prove,<br />then scale.</h2>
          </div>
          <p>The same disciplined approach across all five AI services.</p>
        </Reveal>
        <div className="ea-process">
          {PROCESS_STEPS.map((step, i) => (
            <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-step" key={step.n}>
              <span>{step.n}</span>
              <i>↗</i>
              <h3>{step.t}</h3>
              <p>{step.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* SERVICES GRID — circular icon-badge cards, distinct from the plain
          ea-card grid reused on the individual service pages, since this is
          the primary content of the hub page */}
      <section className="ea-wrap ea-section">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Explore AI services</div>
            <h2>Choose your<br />starting point.</h2>
          </div>
          <p>Each service has its own dedicated page with real detail on approach, process, and fit.</p>
        </Reveal>
        <div className="ab-expertise-grid">
          {aiServicePages.map((s, i) => {
            const Icon = ICONS[s.path] || LuSparkles;
            return (
              <Reveal
                as="a"
                i={(i % 3) + 1}
                reduceMotion={reduceMotion}
                key={s.path}
                className="ab-expertise-card"
                href={s.path}
                onClick={(e) => { e.preventDefault(); navigate(s.path); }}
              >
                <div className="ab-expertise-icon"><Icon aria-hidden="true" /></div>
                <h4>{s.title}</h4>
                <p>{s.desc}</p>
                <span className="ab-expertise-link">Explore this service →</span>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* WHICH SERVICE FITS + FACTS */}
      <section className="ea-models">
        <div className="ea-wrap ea-section">
          <Reveal as="div" className="ea-section-head">
            <div>
              <div className="ea-eyebrow">Still deciding?</div>
              <h2>Match your need<br />to a service.</h2>
            </div>
            <p>The fastest way to find the right starting point.</p>
          </Reveal>
          <div className="ea-model-layout">
            <Reveal as="div" className="ea-model-panel">
              <h3>Which service fits?</h3>
              {FIT_CRITERIA.map((row) => (
                <div key={row.a}><span>{row.a}</span><small>{row.b}</small></div>
              ))}
            </Reveal>
            <div className="ea-metrics">
              {FACTS.map((m, i) => (
                <Reveal as="div" i={i + 1} reduceMotion={reduceMotion} className="ea-metric" key={m.a}>
                  <strong>{m.a}</strong>
                  <span>{m.b}</span>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COST — what drives the price */}
      <section className="ea-wrap ea-section" id="cost">
        <Reveal as="div" className="ea-section-head">
          <div>
            <div className="ea-eyebrow">Cost</div>
            <h2>What drives the cost of<br />AI development.</h2>
          </div>
          <p>Every AI project is priced after scoping, because the same idea can be a few weeks or several months of work. These are the factors that decide it.</p>
        </Reveal>
        <div className="aa-pill-grid">
          {COST_FACTORS.map((item, i) => (
            <Reveal as="div" i={(i % 4) + 1} reduceMotion={reduceMotion} className="ai-industry-card" key={item.t} style={{ flex: "1 1 260px" }}>
              <h3>{item.t}</h3>
              <p>{item.d}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section className="ea-faq-section">
        <div className="ea-wrap ea-faq ea-section">
          <Reveal as="div" className="ea-section-head">
            <div className="ea-eyebrow">Common questions</div>
            <h2>AI development services FAQs</h2>
            <p>Short answers to the questions we hear most about AI projects.</p>
          </Reveal>
          {FAQS.map((item, i) => {
            const isOpen = openFaq === i;
            const panelId = `ai-solutions-faq-panel-${i}`;
            const buttonId = `ai-solutions-faq-button-${i}`;
            return (
              <div className="ea-faq-row" key={item.q}>
                <h3 style={{ margin: 0, fontSize: "inherit", fontWeight: "inherit" }}>
                  <button
                    id={buttonId}
                    aria-expanded={isOpen}
                    aria-controls={panelId}
                    onClick={() => setOpenFaq(isOpen ? -1 : i)}
                  >
                    {item.q}<span>{isOpen ? "−" : "+"}</span>
                  </button>
                </h3>
                <p id={panelId} role="region" aria-labelledby={buttonId} hidden={!isOpen}>{item.a}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* CTA */}
      <section className="ea-cta" aria-labelledby="ai-solutions-cta" style={{ textAlign: "center" }}>
        <div className="ea-wrap">
          <Reveal as="div" reduceMotion={reduceMotion} style={{ maxWidth: 560, margin: "0 auto" }}>
            <div className="ea-eyebrow" style={{ justifyContent: "center" }}>Not sure which fits?</div>
            <h2 id="ai-solutions-cta" style={{ margin: "16px 0" }}>Tell us what you're trying to solve.</h2>
            <p style={{ margin: "0 auto 28px", maxWidth: 460 }}>
              We'll help you figure out which AI service — or combination — actually fits your business.
            </p>
            <button className="ea-button ea-primary" onClick={() => setIsOpen(true)}>
              Start a conversation ↗
            </button>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default AISolutions;
